export const UserType = Object.freeze({
  OWNER: "OWNER",
  PRIMARY_OWNER: "PRIMARY_OWNER",
  ADMIN: "ADMIN",
  SUPER_ADMIN: "SUPER_ADMIN",
  GATE_MANAGER: "GATEMANAGER",
  GATE_CLERK: "GATECLERK",
  DISPATCHER: "DISPATCHER"
});

// TODO: Determine how we can safely integrate ANONYMOUS with the UserType enum above, if at all necessary
// Indicates that a user is not logged in, and therefore does not have a user type associated with {@link UserType}
export const ANONYMOUS_USER_TYPE_NAME = "anonymous";

export const AccountType = Object.freeze({
  BUYER: "Buyer",
  SUPPLIER: "Supplier",
  ADMIN: "Admin"
})

export const SubscriptionType = Object.freeze({
  MARKETPLACE_ONLY: "MARKETPLACE_ONLY",
  GMS_LITE: "GMS_LITE",
  GMS_PRO: "GMS_PRO"
})

export const AuthorityType = Object.freeze({
  ACCOUNT: "ACCOUNT"
})

export const PaymentType = Object.freeze({
  ACH: "ACH",
  CARD: "CARD"
})

export const BankAccountStatus = Object.freeze({
  VERIFIED: 'verified',
  UNVERIFIED: 'unverified'
});

export const DwollaBankAccountType = Object.freeze({
  CHECKING: "Checking",
  SAVINGS: "Savings"
});

export const VerificationType = Object.freeze({
  INSTANT: "INSTANT",
  MICRO_DEPOSIT: "MICRO_DEPOSIT"
})

export const BankAccountType = Object.freeze({
  COMPANY_CHECKING: {value: "COMPANY_CHECKING", label: "Company - Checking"},
  COMPANY_SAVINGS: {value: "COMPANY_SAVINGS", label: "Company - Savings"},
  INDIVIDUAL_CHECKING: {value: "INDIVIDUAL_CHECKING", label: "Individual - Checking"},
  INDIVIDUAL_SAVINGS: {value: "INDIVIDUAL_SAVINGS", label: "Individual - Savings"}
})

export const LogoType = Object.freeze({
  LOGO_TRANSPARENT: "logo-transparent",
  LOGO_WHITE: "logo-white"
})

export const UserTypeName = Object.freeze({
  GATE_CLERK: 'Gate Clerk',
  GATE_MANAGER: 'Gate Manager',
  DISPATCHER: 'Dispatcher',
  ADMIN: 'Administrator',
  ACCOUNT_OWNER: 'Account Owner'
})

export const UserTypeDBFormat = Object.freeze({
  ROLE_USERTYPE_GATECLERK: 'ROLE_USERTYPE_GATECLERK',
  ROLE_USERTYPE_GATEMANAGER: 'ROLE_USERTYPE_GATEMANAGER',
  ROLE_USERTYPE_DISPATCHER: 'ROLE_USERTYPE_DISPATCHER',
  ROLE_USERTYPE_ADMIN: 'ROLE_USERTYPE_ADMIN',
  // SecurSpace super administrator. The backend emits this (see UserType.java and
  // SecurityService.getUserType, which prefers it over ROLE_USERTYPE_ADMIN) but it
  // was missing here, so any check written against this map alone would silently
  // fail to recognise a super admin.
  ROLE_USERTYPE_SUPER_ADMIN: 'ROLE_USERTYPE_SUPER_ADMIN',
  ROLE_USERTYPE_OWNER: 'ROLE_USERTYPE_OWNER',
  ROLE_USERTYPE_PRIMARY_OWNER: 'ROLE_USERTYPE_PRIMARY_OWNER'
})

export const UserRoleToTypeName = Object.freeze({
  [UserTypeDBFormat.ROLE_USERTYPE_GATECLERK]: UserTypeName.GATE_CLERK,
  [UserTypeDBFormat.ROLE_USERTYPE_GATEMANAGER]: UserTypeName.GATE_MANAGER,
  [UserTypeDBFormat.ROLE_USERTYPE_DISPATCHER]: UserTypeName.DISPATCHER,
  [UserTypeDBFormat.ROLE_USERTYPE_ADMIN]: UserTypeName.ADMIN,
  [UserTypeDBFormat.ROLE_USERTYPE_OWNER]: UserTypeName.ADMIN, // NOTE: Owners are displayed as Administrators
  [UserTypeDBFormat.ROLE_USERTYPE_PRIMARY_OWNER]: UserTypeName.ACCOUNT_OWNER
});

/**
 * The constants for BookingReasonDeclined
 * Not guaranteed to be in sync with backend, use {@link getBookingReasonDeclinedOptions} instead.
 * @see {@link BookingReasonDeclinedLabel} for labels
 * @see {@link getBookingReasonDeclinedOptions} for fetching as options
 */
export const BookingReasonDeclined = Object.freeze({
  ACCESS_ISSUES: 'ACCESS_ISSUES',
  AT_CAPACITY: 'AT_CAPACITY',
  AUTO_DECLINED: 'AUTO_DECLINED',
  CUSTOMER_NOT_ELIGIBLE: 'CUSTOMER_NOT_ELIGIBLE',
  EQUIPMENT_NOT_SUPPORTED: 'EQUIPMENT_NOT_SUPPORTED',
  OTHER: 'OTHER',
  PARTNER_DECLINED: 'PARTNER_DECLINED',
  THIRD_PARTY_DECLINED: 'THIRD_PARTY_DECLINED',
});

/**
 * The labels for BookingReasonDeclined
 * Not guaranteed to be in sync with backend, use {@link getBookingReasonDeclinedOptions} instead.
 * @see {@link BookingReasonDeclined} for constants
 * @see {@link getBookingReasonDeclinedOptions} for fetching as options
 */
export const BookingReasonDeclinedLabel = Object.freeze({
  [BookingReasonDeclined.ACCESS_ISSUES]: 'Access or Permit Issues',
  [BookingReasonDeclined.AT_CAPACITY]: 'At Capacity / Scheduling Conflict',
  [BookingReasonDeclined.AUTO_DECLINED]: 'Auto-Declined',
  [BookingReasonDeclined.CUSTOMER_NOT_ELIGIBLE]: 'Customer Not Eligible',
  [BookingReasonDeclined.EQUIPMENT_NOT_SUPPORTED]: 'Equipment Not Supported',
  [BookingReasonDeclined.OTHER]: 'Other (Please Specify)',
  [BookingReasonDeclined.PARTNER_DECLINED]: 'Partner-Declined',
  [BookingReasonDeclined.THIRD_PARTY_DECLINED]: 'Third-Party-Declined',
});

export const DateFormats = Object.freeze({
  DATE: "MM/DD/YYYY hh:mm A",
  DAY: "MM/DD/YYYY",
  MONTH_YEAR: "MM/YYYY",
});

export const BookingSubscriptionStatus = Object.freeze({
  ACTIVE: "Active",
  DECLINED: "Declined",
  CANCELLED: "Cancelled",
  NONE: "None"
});

export const YesNo = Object.freeze({
  YES: "Yes",
  NO: "No",
});

export const BookingDurationType = Object.freeze({
  DAILY: "Daily",
  MONTHLY: "Monthly",
});

export const AssetTypeGroups = Object.freeze({
  CONTAINER_CHASSIS: "Container/Chassis",
  TRUCK_PLUS_CONTAINER_TRAILER: "Truck + Container/Trailer",
  TRUCK_ONLY: "Truck Only",
  TRAILER: "Trailer",
  REEFER: "Refrigeration",
  EQUIPMENT_MOVING_FEES: "Equipment Moving Fee",
  GATE_FEES: "Gate Fees",
  GROUNDED_CONTAINER_FEES: "Grounded Container",
  LIFT_FEES: "Lift Fees",
  OVERAGE_FEES: "Overage Spaces",
  REEFER_FEES: "Reefer KW Usage"
});

export const AssetTypeTaxCode = Object.freeze({
  CONTAINER_CHASSIS_20_40_45: "Container/Chassis (20'/40'/45')",
  CONTAINER_CHASSIS_40_45_53: "Container/Chassis (40'/45'/53')",
  CONTAINER_CHASSIS_20_40: "Container/Chassis (20'/40')",
  CONTAINER_CHASSIS_45: "Container/Chassis (45')",
  TRAILER_53: "Trailer (53')",
  REFRIGERATED_PLUGIN: "Refrigerated Plug In",
  // Parking
  TRUCK_CONTAINER_CHASSIS: "Truck + Container/Chassis",
  TRUCK_TRAILER: "Truck + Trailer",
  TRUCK_ONLY: "Truck Only",
  TRUCK_ONLY_25: "Truck Only (25')",
  // Admin
  EQUIPMENT_MOVING_FEE_PARKING: "Equipment Moving Fee - Parking",
  EQUIPMENT_MOVING_FEE_STORAGE: "Equipment Moving Fee - Storage",
  GATE_FEES_PARKING: "Gate Fees - Parking",
  GATE_FEES_STORAGE: "Gate Fees - Storage",
  GROUNDED_CONTAINER_EMPTY: "Grounded Container: Empty",
  GROUNDED_CONTAINER_LOADED: "Grounded Container: Loaded",
  LIFT_FEES_EMPTY: "Lift Fees: Empty",
  LIFT_FEES_LOADED: "Lift Fees: Loaded",
  OVERAGES_PARKING: "Overage Spaces - Parking",
  OVERAGES_STORAGE: "Overage Spaces - Storage",
  REEFER_KW_USAGE: "Reefer KW Usage",
});

export const BookingStatus = Object.freeze({
  INCOMPLETE: "Incomplete",
  PENDING: "Pending",
  PENDING_VERIFICATION_REQUIRED: "Pending-Verification Required",
  APPROVED: "Approved",
  APPROVED_ADJUSTED: "Approved-Adjusted",
  DECLINED: "Declined",
  CANCELLED: "Cancelled",
  MANUAL_PAYMENT: "Manual-Payment",
  EXPIRED: "Expired",
  ERROR: "Error",
  PROCESSING_ACH_PAYMENT: "Processing-ACH-Payment",
  PROCESSING_DATE_ADJUST_PENDING_ACH_PAYMENT: "Processing-ACH-Payment-Adjusted",
  PROCESSING_DATE_ADJUST_PENDING_ACH_REFUND: "Processing-ACH-Refund-Adjusted",
  PAYMENT_DECLINED: "Payment Declined",
  PAYMENT_DECLINED_ADJUSTED: "Payment Declined-Adjusted",
  REFUND_DECLINED_ADJUSTED: "Refund Declined-Adjusted",
});

export const BookingStatusType = Object.freeze({
  APPROVED: "APPROVED",
  APPROVED_ADJUSTED: "APPROVED_ADJUSTED",
  CANCELLED: "CANCELLED",
  DECLINED: "DECLINED",
  ERROR: "ERROR",
  INCOMPLETE: "INCOMPLETE",
  MANUAL_PAYMENT: "MANUAL_PAYMENT",
  PAYMENT_DECLINED: "PAYMENT_DECLINED",
  PAYMENT_DECLINED_ADJUSTED: "PAYMENT_DECLINED_ADJUSTED",
  PENDING: "PENDING",
  PENDING_VERIFICATION_REQUIRED: "PENDING_VERIFICATION_REQUIRED",
  PROCESSING_ACH_PAYMENT: "PROCESSING_ACH_PAYMENT",
  PROCESSING_DATE_ADJUST_PENDING_ACH_PAYMENT: "PROCESSING_DATE_ADJUST_PENDING_ACH_PAYMENT",
  PROCESSING_DATE_ADJUST_PENDING_ACH_REFUND: "PROCESSING_DATE_ADJUST_PENDING_ACH_REFUND",
  REFUND_DECLINED_ADJUSTED: "REFUND_DECLINED_ADJUSTED",
});

export const BookingStatusesEligibleForCancellation = Object.freeze([
  BookingStatus.APPROVED,
  BookingStatus.APPROVED_ADJUSTED,
  BookingStatus.PAYMENT_DECLINED,
  BookingStatus.PAYMENT_DECLINED_ADJUSTED,
  BookingStatus.PROCESSING_ACH_PAYMENT,
  BookingStatus.MANUAL_PAYMENT,
]);

export const BookingStatusesEligibleForPaymentMethodUpdate = Object.freeze([
  BookingStatus.APPROVED,
  BookingStatus.APPROVED_ADJUSTED,
  BookingStatus.PAYMENT_DECLINED_ADJUSTED,
]);

export const BookingStatusesEligibleForMakingPayment = Object.freeze([
  BookingStatus.PAYMENT_DECLINED,
]);

export const EquipmentLengthsConstant = Object.freeze({
  TWENTY_FIVE_FOOT: "25' MAX (Truck Only)",
  FORTY_FIVE_FOOT: "45' MAX (Small Container)",
  FIFTY_THREE_FOOT: "53' MAX (Large Container)",
  EIGHTY_THREE_FOOT: "83' MAX (Truck + Trailer)"
});

export const invoiceStatusesThatCanBeCancelled = [
  "PAYMENT_AUTHORIZED",
  "PAYMENT_NOT_SUBMITTED",
  "PAYMENT_CANCELLED",
  "PAYMENT_FAILED",
  "SECURSPACE_PAYOUT_FAILED",
  "SECURSPACE_PAYOUT_CANCELED",
  "SUPPLIER_PAYOUT_FAILED_SECURSPACE_FEE_PAYOUT_SUCCEEDED",
  "SUPPLIER_PAYOUT_FAILED",
  "MANUAL_PAYMENT",
  "DECLINED",
];

export const invoicesThatCanBeResent = [
  "DECLINED",
];

export const invoiceCancelStatuses = {
  UNCOLLECTIBLE: 'UNCOLLECTIBLE',
  CANCEL_TERM_CHANGES: 'CANCEL_TERM_CHANGES',
  CANCEL_BOOKING_CANCELLED: 'CANCEL_BOOKING_CANCELLED',
  CANCEL_BILLING_REQUIREMENTS: 'CANCEL_BILLING_REQUIREMENTS',
  CANCEL_OTHER: 'CANCEL_OTHER'
};

export const ServiceFeeType = Object.freeze({
  LIFT_FEE: 'LIFT_FEE',
  GATE_FEE: 'GATE_FEE',
  VALET_MOVING_FEE: 'VALET_MOVING_FEE',
  TRANSLOADING: 'TRANSLOADING',
  CONTAINER_FREIGHT_STABILIZATION: 'CONTAINER_FREIGHT_STABILIZATION',
  DRAYAGE: 'DRAYAGE',
  EV_CHARGING: 'EV_CHARGING',
  MUDFLAP_REPAIR: 'MUDFLAP_REPAIR',
  TRUCK_OR_TRAILER_WASH: 'TRUCK_OR_TRAILER_WASH',
  HEADLIGHT_TAILLIGHT_REPAIR_REPLACEMENT: 'HEADLIGHT_TAILLIGHT_REPAIR_REPLACEMENT',
  TIRE_REPAIR_REPLACEMENT: 'TIRE_REPAIR_REPLACEMENT',
  OIL_CHANGE: 'OIL_CHANGE',
  GENERAL_CHASSIS_REPAIR: 'GENERAL_CHASSIS_REPAIR',
  GENERAL_CONTAINER_REPAIR: 'GENERAL_CONTAINER_REPAIR',
});

export const ServiceFeeTypeLabel = Object.freeze({
  [ServiceFeeType.LIFT_FEE]: 'Lift Fee',
  [ServiceFeeType.GATE_FEE]: 'Gate Fee',
  [ServiceFeeType.VALET_MOVING_FEE]: 'Valet/Moving Fee',
  [ServiceFeeType.TRANSLOADING]: 'Transloading',
  [ServiceFeeType.CONTAINER_FREIGHT_STABILIZATION]: 'Container Freight Stabilization',
  [ServiceFeeType.DRAYAGE]: 'Drayage',
  [ServiceFeeType.EV_CHARGING]: 'EV Charging',
  [ServiceFeeType.MUDFLAP_REPAIR]: 'Mudflap Repair',
  [ServiceFeeType.TRUCK_OR_TRAILER_WASH]: 'Truck or Trailer Wash',
  [ServiceFeeType.HEADLIGHT_TAILLIGHT_REPAIR_REPLACEMENT]: 'Headlight/taillight Repair/Replacement',
  [ServiceFeeType.TIRE_REPAIR_REPLACEMENT]: 'Tire Repair/Replacement',
  [ServiceFeeType.OIL_CHANGE]: 'Oil Change',
  [ServiceFeeType.GENERAL_CHASSIS_REPAIR]: 'General Chassis Repair',
  [ServiceFeeType.GENERAL_CONTAINER_REPAIR]: 'General Container Repair',
});

export const MAINTENANCE_AND_REPAIR_LABEL = Object.freeze('Maintenance and Repair');

export const MaintenanceAndRepairSubTypes = Object.freeze([
  ServiceFeeType.MUDFLAP_REPAIR,
  ServiceFeeType.TRUCK_OR_TRAILER_WASH,
  ServiceFeeType.HEADLIGHT_TAILLIGHT_REPAIR_REPLACEMENT,
  ServiceFeeType.TIRE_REPAIR_REPLACEMENT,
  ServiceFeeType.OIL_CHANGE,
  ServiceFeeType.GENERAL_CHASSIS_REPAIR,
  ServiceFeeType.GENERAL_CONTAINER_REPAIR,
]);

export const BookingTransactionType = Object.freeze({
  BOOKING_CHARGE: 'BOOKING_CHARGE',
  CANCELLATION_REFUND: 'CANCELLATION_REFUND',
  SPACE_REMOVED_REFUND: 'SPACE_REMOVED_REFUND',
  DATE_ADJUST_CHARGE: 'DATE_ADJUST_CHARGE',
  DATE_ADJUST_REFUND: 'DATE_ADJUST_REFUND',
  ADD_SPACE_CHARGE: 'ADD_SPACE_CHARGE',
  OVERAGE_CHARGE: 'OVERAGE_CHARGE',
  DAILY_OVERAGE_CHARGE: 'DAILY_OVERAGE_CHARGE',
  OVERSTAY_CHARGE: 'OVERSTAY_CHARGE',
  MANUAL_OVERAGE_CHARGE: 'MANUAL_OVERAGE_CHARGE',
  BILLING_CORRECTION_REFUND: 'BILLING_CORRECTION_REFUND',
  SERVICE_CHARGE: 'SERVICE_CHARGE',
  DISPUTE_COLLECTION: "DISPUTE_COLLECTION",
  AGS_FEE: 'AGS_FEE',
});

export const BookingTransactionTypeLabel = Object.freeze({
  [BookingTransactionType.BOOKING_CHARGE]: 'Booking Payment',
  [BookingTransactionType.CANCELLATION_REFUND]: 'Cancellation Refund',
  [BookingTransactionType.SPACE_REMOVED_REFUND]: 'Space Removed Refund',
  [BookingTransactionType.DATE_ADJUST_CHARGE]: 'Date Adjust Payment',
  [BookingTransactionType.DATE_ADJUST_REFUND]: 'Date Adjust Refund',
  [BookingTransactionType.ADD_SPACE_CHARGE]: 'Add Space Charge',
  [BookingTransactionType.OVERAGE_CHARGE]: 'Overage Payment',
  [BookingTransactionType.DAILY_OVERAGE_CHARGE]: 'Daily Overage Payment',
  [BookingTransactionType.OVERSTAY_CHARGE]: 'Overstay Payment',
  [BookingTransactionType.MANUAL_OVERAGE_CHARGE]: 'Manual Overage Payment',
  [BookingTransactionType.BILLING_CORRECTION_REFUND]: 'Billing Correction Refund',
  [BookingTransactionType.SERVICE_CHARGE]: 'Service Charge',
  [BookingTransactionType.DISPUTE_COLLECTION]: "Dispute Collection",
  [BookingTransactionType.AGS_FEE]: 'AGS Fee',
});

export const BookingTransactionStatusType = Object.freeze({
  BOOKING_INCOMPLETE: 'BOOKING_INCOMPLETE',
  CHARGE_PENDING: 'CHARGE_PENDING',
  CHARGE_ON_HOLD: 'CHARGE_ON_HOLD',
  PAYMENT_NOT_SUBMITTED: 'PAYMENT_NOT_SUBMITTED',
  REFUND_REQUESTED: 'REFUND_REQUESTED',
  REFUND_DENIED: 'REFUND_DENIED',
  CANCELLATION_REFUND: 'CANCELLATION_REFUND',
  SPACE_REMOVED_REFUND: 'SPACE_REMOVED_REFUND',
  DATE_ADJUST_REFUND: 'DATE_ADJUST_REFUND',
  BILLING_CORRECTION_REFUND: 'BILLING_CORRECTION_REFUND',
  MANUAL_PAYMENT: 'MANUAL_PAYMENT',
  PAYMENT_AUTHORIZED: 'PAYMENT_AUTHORIZED',
  PAYMENT_CANCELLED: 'PAYMENT_CANCELLED',
  PAYMENT_PENDING: 'PAYMENT_PENDING',
  PAYMENT_SUCCEEDED: 'PAYMENT_SUCCEEDED',
  PAYMENT_FAILED: 'PAYMENT_FAILED',
  SECURSPACE_PAYOUT_PENDING: 'SECURSPACE_PAYOUT_PENDING',
  SECURSPACE_PAYOUT_SUCCEEDED: 'SECURSPACE_PAYOUT_SUCCEEDED',
  SECURSPACE_PAYOUT_FAILED: 'SECURSPACE_PAYOUT_FAILED',
  SECURSPACE_PAYOUT_CANCELED: 'SECURSPACE_PAYOUT_CANCELED',
  SUPPLIER_PAYOUT_INITIATING: 'SUPPLIER_PAYOUT_INITIATING',
  SUPPLIER_PAYOUT_PENDING: 'SUPPLIER_PAYOUT_PENDING',
  SUPPLIER_PAYOUT_ON_HOLD: 'SUPPLIER_PAYOUT_ON_HOLD',
  SUPPLIER_PAYOUT_SUCCEEDED: 'SUPPLIER_PAYOUT_SUCCEEDED',
  SUPPLIER_PAYOUT_FAILED_SECURSPACE_FEE_PAYOUT_SUCCEEDED: 'SUPPLIER_PAYOUT_FAILED_SECURSPACE_FEE_PAYOUT_SUCCEEDED',
  SUPPLIER_PAYOUT_SUCCEEDED_SECURSPACE_FEE_PAYOUT_FAILED: 'SUPPLIER_PAYOUT_SUCCEEDED_SECURSPACE_FEE_PAYOUT_FAILED',
  SUPPLIER_PAYOUT_FAILED: 'SUPPLIER_PAYOUT_FAILED',
  UNCOLLECTIBLE: 'UNCOLLECTIBLE',
  CANCEL_TERM_CHANGES: 'CANCEL_TERM_CHANGES',
  CANCEL_BOOKING_CANCELLED: 'CANCEL_BOOKING_CANCELLED',
  CANCEL_BILLING_REQUIREMENTS: 'CANCEL_BILLING_REQUIREMENTS',
  CANCEL_OTHER: 'CANCEL_OTHER',
  DECLINED: 'DECLINED',
});

export const BookingTransactionStatusTypeLabel = Object.freeze({
  [BookingTransactionStatusType.CHARGE_PENDING]: 'Charge Pending',
  [BookingTransactionStatusType.BOOKING_INCOMPLETE]: 'Booking Incomplete',
  [BookingTransactionStatusType.CHARGE_ON_HOLD]: 'Charge On Hold',
  [BookingTransactionStatusType.PAYMENT_NOT_SUBMITTED]: 'Payment Not Submitted',
  [BookingTransactionStatusType.REFUND_REQUESTED]: 'Refund Requested',
  [BookingTransactionStatusType.REFUND_DENIED]: 'Refund Denied',
  [BookingTransactionStatusType.MANUAL_PAYMENT]: 'Manual Payment',
  [BookingTransactionStatusType.PAYMENT_AUTHORIZED]: 'Payment Authorized',
  [BookingTransactionStatusType.PAYMENT_CANCELLED]: 'Payment Cancelled',
  [BookingTransactionStatusType.PAYMENT_PENDING]: 'Payment Pending',
  [BookingTransactionStatusType.PAYMENT_SUCCEEDED]: 'Payment Succeeded',
  [BookingTransactionStatusType.PAYMENT_FAILED]: 'Payment Failed',
  [BookingTransactionStatusType.SECURSPACE_PAYOUT_PENDING]: 'SecurSpace Payout Pending',
  [BookingTransactionStatusType.SECURSPACE_PAYOUT_SUCCEEDED]: 'SecurSpace Payout Succeeded',
  [BookingTransactionStatusType.SECURSPACE_PAYOUT_FAILED]: 'SecurSpace Payout Failed',
  [BookingTransactionStatusType.SECURSPACE_PAYOUT_CANCELED]: 'SecurSpace Payout Cancelle',
  [BookingTransactionStatusType.SUPPLIER_PAYOUT_INITIATING]: 'Supplier Payout Initiating',
  [BookingTransactionStatusType.SUPPLIER_PAYOUT_PENDING]: 'Supplier Payout Pending',
  [BookingTransactionStatusType.SUPPLIER_PAYOUT_ON_HOLD]: 'Supplier Payout On Hold',
  [BookingTransactionStatusType.SUPPLIER_PAYOUT_SUCCEEDED]: 'Supplier Payout Succeeded',
  [BookingTransactionStatusType.SUPPLIER_PAYOUT_FAILED_SECURSPACE_FEE_PAYOUT_SUCCEEDED]: 'Supplier Payout Failed, SecurSpace Fee Payout Succeeded',
  [BookingTransactionStatusType.SUPPLIER_PAYOUT_SUCCEEDED_SECURSPACE_FEE_PAYOUT_FAILED]: 'Supplier Payout Succeeded, SecurSpace Fee Payout Failed',
  [BookingTransactionStatusType.SUPPLIER_PAYOUT_FAILED]: 'Supplier Payout Failed',
  [BookingTransactionStatusType.UNCOLLECTIBLE]: 'Uncollectible',
  [BookingTransactionStatusType.CANCEL_TERM_CHANGES]: 'Cancelled: Term Changes',
  [BookingTransactionStatusType.CANCEL_BOOKING_CANCELLED]: 'Cancelled: Booking Cancelled',
  [BookingTransactionStatusType.CANCEL_BILLING_REQUIREMENTS]: 'Cancelled: Billing Requirements',
  [BookingTransactionStatusType.CANCEL_OTHER]: 'Cancelled',
  [BookingTransactionStatusType.DECLINED]: 'Declined',
});

export const buyerServiceFeeStatusesThatArePending = [
  BookingTransactionStatusType.CHARGE_PENDING,
  BookingTransactionStatusType.PAYMENT_NOT_SUBMITTED,
  BookingTransactionStatusType.PAYMENT_AUTHORIZED,
  BookingTransactionStatusType.PAYMENT_PENDING,
];

export const buyerServiceFeeStatusesThatAreComplete = [
  BookingTransactionStatusType.MANUAL_PAYMENT,
  BookingTransactionStatusType.PAYMENT_CANCELLED,
  BookingTransactionStatusType.PAYMENT_SUCCEEDED,
  BookingTransactionStatusType.PAYMENT_FAILED,
  BookingTransactionStatusType.SECURSPACE_PAYOUT_PENDING,
  BookingTransactionStatusType.SECURSPACE_PAYOUT_SUCCEEDED,
  BookingTransactionStatusType.SECURSPACE_PAYOUT_FAILED,
  BookingTransactionStatusType.SECURSPACE_PAYOUT_CANCELED,
  BookingTransactionStatusType.SUPPLIER_PAYOUT_INITIATING,
  BookingTransactionStatusType.SUPPLIER_PAYOUT_PENDING,
  BookingTransactionStatusType.SUPPLIER_PAYOUT_ON_HOLD,
  BookingTransactionStatusType.SUPPLIER_PAYOUT_SUCCEEDED,
  BookingTransactionStatusType.SUPPLIER_PAYOUT_FAILED_SECURSPACE_FEE_PAYOUT_SUCCEEDED,
  BookingTransactionStatusType.SUPPLIER_PAYOUT_SUCCEEDED_SECURSPACE_FEE_PAYOUT_FAILED,
  BookingTransactionStatusType.SUPPLIER_PAYOUT_FAILED,
];

export const supplierPaidOutBookingTransactionStatuses = [
    BookingTransactionStatusType.SUPPLIER_PAYOUT_PENDING,
    BookingTransactionStatusType.SUPPLIER_PAYOUT_ON_HOLD,
    BookingTransactionStatusType.SUPPLIER_PAYOUT_SUCCEEDED,
    BookingTransactionStatusType.SUPPLIER_PAYOUT_SUCCEEDED_SECURSPACE_FEE_PAYOUT_FAILED
];

export const refundableBookingTransactionStatuses = [
    BookingTransactionStatusType.PAYMENT_SUCCEEDED,
    BookingTransactionStatusType.SECURSPACE_PAYOUT_PENDING,
    BookingTransactionStatusType.SECURSPACE_PAYOUT_SUCCEEDED,
    BookingTransactionStatusType.SECURSPACE_PAYOUT_FAILED,
    BookingTransactionStatusType.SECURSPACE_PAYOUT_CANCELED,
    BookingTransactionStatusType.SUPPLIER_PAYOUT_FAILED_SECURSPACE_FEE_PAYOUT_SUCCEEDED,
    BookingTransactionStatusType.SUPPLIER_PAYOUT_FAILED
];

export const excludedFromPayoutBookingTransactionStatuses = [
    BookingTransactionStatusType.REFUND_REQUESTED,
    BookingTransactionStatusType.SUPPLIER_PAYOUT_INITIATING,
    BookingTransactionStatusType.SUPPLIER_PAYOUT_PENDING,
    BookingTransactionStatusType.SUPPLIER_PAYOUT_ON_HOLD,
    BookingTransactionStatusType.SUPPLIER_PAYOUT_SUCCEEDED,
    BookingTransactionStatusType.SUPPLIER_PAYOUT_FAILED_SECURSPACE_FEE_PAYOUT_SUCCEEDED,
    BookingTransactionStatusType.SUPPLIER_PAYOUT_SUCCEEDED_SECURSPACE_FEE_PAYOUT_FAILED,
    BookingTransactionStatusType.SUPPLIER_PAYOUT_FAILED
];

export const refundableBookingTransactionTypes = [
    BookingTransactionType.BOOKING_CHARGE,
    BookingTransactionType.DATE_ADJUST_CHARGE,
    BookingTransactionType.ADD_SPACE_CHARGE,
    BookingTransactionType.OVERAGE_CHARGE,
    BookingTransactionType.DAILY_OVERAGE_CHARGE,
    BookingTransactionType.OVERSTAY_CHARGE
];

export const LOGIN_PATH = Object.freeze('/login');

export const LoginRedirectPathnameBlockList = Object.freeze([
  '/',
  LOGIN_PATH,
  '/logout', // lol
  '/how-it-works',
  '/about',
  '/faq',
  '/gms-landing',
  '/supplier-signup',
  '/buyer-signup',
  '/external-buyer-signup',
  '/search',
  '/change-password',
  '/set-password',
  '/blog',
  '/verify'
]);

export const LoginRedirectNestedPathnameBlockList = Object.freeze([
  '/location-profile',
  '/yards',
  '/supplier',
  '/terms-of-use',
  '/privacy-policy',
  '/blog',
  '/account',
]);

export const LoginRedirectedNestedPathnameExplicitlyAllowedList = Object.freeze([
  '/account/payment-methods',
]);

export const externalRoutePathnames = Object.freeze([
  '/external-buyer-signup'
]);

export const PartnerSubscriptionTypes = Object.freeze({
  AGS_FEE: 'AGS_FEE',
  NO_SUBSCRIPTION: 'NO SUBSCRIPTION'
});

export const PartnerSubscriptionTypeLabel = Object.freeze({
  [PartnerSubscriptionTypes.AGS_FEE]: 'AGS FEE',
  [PartnerSubscriptionTypes.NO_SUBSCRIPTION]: 'NO SUBSCRIPTION',
});
export const DAYS_PER_MONTH = 30.42;

// trying out new combination of flow and proptypes for better type completion on text based attributes
// flow utilities aren't quite as good as typescript, so using a map with meaningless numbers here to convert to type
export const severityColorMap = Object.freeze({
  success: 0,
  info: 1,
  warning: 2,
  error: 3,
});
// this array is used since i can't pass flow types into proptype typings
export const severityColors = Object.keys(severityColorMap);
export type SeverityColor = keyof typeof severityColorMap;

export const PartnerSubscriptionTransactionStatusType = Object.freeze({
  CHARGE_SUCCEEDED: 'CHARGE_SUCCEEDED',
  CHARGE_PENDING: 'CHARGE_PENDING',
  CHARGE_FAILED: 'CHARGE_FAILED',
  CHARGE_CANCELLED: 'CHARGE_CANCELLED',
});

export const PartnerSubscriptionTransactionStatusTypeLabel = Object.freeze({
  [PartnerSubscriptionTransactionStatusType.CHARGE_SUCCEEDED]: 'Charge Succeeded',
  [PartnerSubscriptionTransactionStatusType.CHARGE_PENDING]: 'Charge Pending',
  [PartnerSubscriptionTransactionStatusType.CHARGE_FAILED]: 'Charge Failed',
  [PartnerSubscriptionTransactionStatusType.CHARGE_CANCELLED]: 'Cancelled',
});

export const CheckInTypesFilter = Object.freeze([
  "Overage Spaces",
  "Lift Fees: Loaded",
  "Lift Fees: Empty",
  "Gate Fees",
  "Reefer KW Usage",
  "Equipment Moving Fee"
]);

export const AccountTypes = Object.freeze({
  INDIVIDUAL_CHECKING: "INDIVIDUAL_CHECKING",
  COMPANY_CHECKING: "COMPANY_CHECKING",
  INDIVIDUAL_SAVINGS: "INDIVIDUAL_SAVINGS",
  COMPANY_SAVINGS: "COMPANY_SAVINGS",
});

export const AccountTypeLabel = Object.freeze({
  [AccountTypes.INDIVIDUAL_CHECKING]: "Individual - Checking",
  [AccountTypes.COMPANY_CHECKING]: "Company - Checking",
  [AccountTypes.INDIVIDUAL_SAVINGS]: "Individual - Savings",
  [AccountTypes.COMPANY_SAVINGS]: "Company - Savings",
});

export const imageRepositoryBaseURL = 'https://s3-us-west-1.amazonaws.com/securspace-files';
export const listingImageBaseURL = imageRepositoryBaseURL + '/listing-images/';

export const payoutScheduleOptions = Object.freeze([
  {value: "DAILY", label: "Daily"},
  {value: "WEEKLY", label: "Weekly"},
  {value: "MONTHLY", label: "Monthly"}
]);

export const subscriptionTypeOptions = Object.freeze([
  {value: SubscriptionType.MARKETPLACE_ONLY, label: "Marketplace Only"},
  {value: SubscriptionType.GMS_LITE, label: "GMS Lite"},
  {value: SubscriptionType.GMS_PRO, label: "GMS Pro"}
]);

export const inventoryCorrectionReasons = Object.freeze({
  ASSET_DOES_NOT_BELONG_TO_BUYER: "ASSET_DOES_NOT_BELONG_TO_BUYER",
  ASSET_CHECKED_OUT_ON_WRONG_DATE: "ASSET_CHECKED_OUT_ON_WRONG_DATE",
  ASSET_NUMBER_ENTERED_INCORRECTLY: "ASSET_NUMBER_ENTERED_INCORRECTLY",
  ASSET_LEFT_THE_YARD: "ASSET_LEFT_THE_YARD",
});

export const InventoryCorrectionReasonLabels = Object.freeze([
  {value: inventoryCorrectionReasons.ASSET_DOES_NOT_BELONG_TO_BUYER, label: "Asset does not belong to me"},
  {value: inventoryCorrectionReasons.ASSET_CHECKED_OUT_ON_WRONG_DATE, label: "Asset was checked out on the wrong date"},
  {value: inventoryCorrectionReasons.ASSET_NUMBER_ENTERED_INCORRECTLY, label: "Asset number was entered incorrectly"},
  {value: inventoryCorrectionReasons.ASSET_LEFT_THE_YARD, label: "Asset left the yard"}
]);

export const InventoryCorrectionCheckinReasonLabels = Object.freeze([
  {value: inventoryCorrectionReasons.ASSET_NUMBER_ENTERED_INCORRECTLY, label: "Asset number was entered incorrectly"},
  {value: inventoryCorrectionReasons.ASSET_LEFT_THE_YARD, label: "Asset left the yard"}
]);

export const InventoryCorrectionCheckinCheckoutReasonLabels = Object.freeze([
  {value: inventoryCorrectionReasons.ASSET_NUMBER_ENTERED_INCORRECTLY, label: "Asset number was entered incorrectly"},
  {value: inventoryCorrectionReasons.ASSET_CHECKED_OUT_ON_WRONG_DATE, label: "Asset was checked out on the wrong date"},
]);

export const inventoryCorrectionStatus = Object.freeze({
  PENDING: "PENDING",
  APPROVED: "APPROVED",
  REJECTED: "REJECTED",
  CANCELLED: "CANCELLED"
})

export const InterchangeActivityType = Object.freeze({CHECK_IN: "CHECK_IN", CHECK_OUT: "CHECK_OUT"});

export const InterchangeActivityTypeLabel = Object.freeze({
  [InterchangeActivityType.CHECK_IN]: "Check In",
  [InterchangeActivityType.CHECK_OUT]: "Check Out",
});

export const AssetType = Object.freeze({
  CONTAINER_LOADED: "CONTAINER_LOADED",
  CONTAINER_EMPTY: "CONTAINER_EMPTY",
  CHASSIS_ONLY: "CHASSIS_ONLY",
  TRUCK_CONTAINER_LOADED: "TRUCK_CONTAINER_LOADED",
  TRUCK_TRAILER_LOADED: "TRUCK_TRAILER_LOADED",
  TRUCK_CONTAINER_EMPTY: "TRUCK_CONTAINER_EMPTY",
  TRUCK_TRAILER_EMPTY: "TRUCK_TRAILER_EMPTY",
  TRUCK_ONLY: "TRUCK_ONLY",
  TRUCK_ONLY_25: "TRUCK_ONLY_25",
  TRAILER_LOADED: "TRAILER_LOADED",
  TRAILER_EMPTY: "TRAILER_EMPTY",
  REEFER_LOADED_PLUGIN: "REEFER_LOADED_PLUGIN",
  REEFER_LOADED_NO_PLUGIN: "REEFER_LOADED_NO_PLUGIN",
  TRUCK_REEFER_LOADED_PLUGIN: "TRUCK_REEFER_LOADED_PLUGIN",
  TRUCK_REEFER_LOADED_NO_PLUGIN: "TRUCK_REEFER_LOADED_NO_PLUGIN",
  ALL: 'ALL'
});

export const AssetTypeLabel = Object.freeze({
  [AssetType.CONTAINER_LOADED]: "Container / Chassis (Loaded)",
  [AssetType.CONTAINER_EMPTY]: "Container / Chassis (Empty)",
  [AssetType.CHASSIS_ONLY]: "Chassis Only",
  [AssetType.TRUCK_CONTAINER_LOADED]: "Truck + Container / Chassis (Loaded)",
  [AssetType.TRUCK_TRAILER_LOADED]: "Truck + Trailer (Loaded)",
  [AssetType.TRUCK_CONTAINER_EMPTY]: "Truck + Container / Chassis (Empty)",
  [AssetType.TRUCK_TRAILER_EMPTY]: "Truck + Trailer (Empty)",
  [AssetType.TRUCK_ONLY]: "Truck Only",
  [AssetType.TRUCK_ONLY_25]: "Truck Only (25')",
  [AssetType.TRAILER_LOADED]: "Trailer (Loaded)",
  [AssetType.TRAILER_EMPTY]: "Trailer (Empty)",
  [AssetType.REEFER_LOADED_PLUGIN]: "Refrigerated (Loaded) (Plug In)",
  [AssetType.REEFER_LOADED_NO_PLUGIN]: "Reefer (Loaded) (No Plug In)",
  [AssetType.TRUCK_REEFER_LOADED_PLUGIN]: "Truck + Reefer (Loaded) (Plug In)",
  [AssetType.TRUCK_REEFER_LOADED_NO_PLUGIN]: "Truck + Reefer (Loaded) (No Plug In)"
});

export const assetSizes = Object.freeze(["20", "40", "45", "50", "53"]);

export const InvalidServiceFeeChargeTypes = Object.freeze([
  BookingStatus.MANUAL_PAYMENT,
  BookingStatus.PAYMENT_DECLINED,
  BookingStatus.INCOMPLETE,
  BookingStatus.PROCESSING_ACH_PAYMENT,
  BookingStatus.PROCESSING_DATE_ADJUST_PENDING_ACH_PAYMENT,
  BookingStatus.PENDING,
  BookingStatus.DECLINED,
  BookingStatus.CANCELLED
]);

export const RefundTypes = Object.freeze([
  BookingTransactionStatusType.REFUND_REQUESTED,
  BookingTransactionStatusType.CANCELLATION_REFUND,
  BookingTransactionStatusType.SPACE_REMOVED_REFUND,
  BookingTransactionStatusType.DATE_ADJUST_REFUND,
  BookingTransactionStatusType.BILLING_CORRECTION_REFUND,
]);

export const securSpacePlatforms = Object.freeze({
  LOCAL: {
    name: 'local',
    urlTest: /^http:\/\/localhost/,
    hostnameTest: /^localhost$/,
  },
  TEST: {
    name: 'test',
    urlTest: /^https:\/\/test\.secur\.space/,
    hostnameTest: /^test\.secur\.space$/,
  },
  SANDBOX: {
    name: 'sandbox',
    urlTest: /^https:\/\/sandbox\.secur\.space/,
    hostnameTest: /^sandbox\.secur\.space$/,
  },
  PRODUCTION: {
    name: 'prod',
    urlTest: /^https:\/\/secur\.space/,
    hostnameTest: /^secur\.space$/,
  },
});

export const GateEventType = Object.freeze({
  CHECK_IN: "Check In",
  CHECK_OUT: "Check Out",
});

export const PendingInventoryActivityStatus = Object.freeze({
  PENDING: "Pending",
  RESOLVED: "Resolved",
  DISMISSED: "Dismissed",
  EXPIRED: "Expired",
});

export const GateEventValidationError = Object.freeze({
  BOOKING_NOT_FOUND: "Booking Not Found",
  BOOKING_EXPIRED: "Booking Expired",
  DUPLICATE_EQUIPMENT: "Duplicate Equipment",
  INVALID_ASSET_TYPE: "Invalid Asset Type",
  CHECKOUT_NO_MATCH: "No Matching Check-In",
  MISSING_REQUIRED_FIELD: "Missing Required Field",
  NO_GATE_EVENT: "No Associated Gate Event",
  PROCESSING_FAILED: "Processing Failed",
  UNKNOWN_ERROR: "Unknown Error",
});
